/**
 * Favicon Generator Service
 * Generates complete favicon packages from any image
 */

import JSZip from 'jszip';

export interface FaviconConfig {
  name: string;
  size: number;
  format: 'png' | 'ico';
}

export interface GeneratedFavicon {
  name: string;
  size: number;
  blob: Blob;
  dataUrl: string;
}

// Standard favicon sizes
export const FAVICON_SIZES: FaviconConfig[] = [
  { name: 'favicon-16x16.png', size: 16, format: 'png' },
  { name: 'favicon-32x32.png', size: 32, format: 'png' },
  { name: 'favicon-48x48.png', size: 48, format: 'png' },
  { name: 'apple-touch-icon.png', size: 180, format: 'png' },
  { name: 'android-chrome-192x192.png', size: 192, format: 'png' },
  { name: 'android-chrome-512x512.png', size: 512, format: 'png' },
  { name: 'mstile-150x150.png', size: 150, format: 'png' },
];

/**
 * Generate all favicon sizes from an image file
 */
export async function generateFavicons(file: File): Promise<GeneratedFavicon[]> {
  const img = await loadImage(file);
  const favicons: GeneratedFavicon[] = [];

  for (const config of FAVICON_SIZES) {
    const { blob, dataUrl } = await resizeImage(img, config.size);
    favicons.push({
      name: config.name,
      size: config.size,
      blob,
      dataUrl,
    });
  }

  return favicons;
}

/**
 * Load an image file into an HTMLImageElement
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Resize image to specified size
 */
async function resizeImage(
  img: HTMLImageElement, 
  size: number
): Promise<{ blob: Blob; dataUrl: string }> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) throw new Error('Canvas not supported');

  canvas.width = size;
  canvas.height = size;

  // Enable image smoothing for better quality
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Calculate crop to make square (center crop)
  const srcSize = Math.min(img.width, img.height);
  const srcX = (img.width - srcSize) / 2;
  const srcY = (img.height - srcSize) / 2;

  // Draw resized image
  ctx.drawImage(img, srcX, srcY, srcSize, srcSize, 0, 0, size, size);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve({
            blob,
            dataUrl: canvas.toDataURL('image/png'),
          });
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      'image/png',
      1.0
    );
  });
}

/**
 * Generate ICO file from multiple sizes
 */
export async function generateIco(favicons: GeneratedFavicon[]): Promise<Blob> {
  // Get 16, 32, and 48 px versions for ICO
  const sizes = [16, 32, 48];
  const images: { size: number; data: Uint8Array }[] = [];

  for (const size of sizes) {
    const favicon = favicons.find(f => f.size === size);
    if (favicon) {
      const arrayBuffer = await favicon.blob.arrayBuffer();
      images.push({ size, data: new Uint8Array(arrayBuffer) });
    }
  }

  // Create ICO file structure
  const icoData = createIcoFile(images);
  return new Blob([icoData.buffer as ArrayBuffer], { type: 'image/x-icon' });
}

/**
 * Create ICO file from PNG data
 * ICO format: ICONDIR header + ICONDIRENTRY for each image + image data
 */
function createIcoFile(images: { size: number; data: Uint8Array }[]): Uint8Array {
  // Calculate total size
  const headerSize = 6; // ICONDIR
  const entrySize = 16; // ICONDIRENTRY per image
  const entriesSize = entrySize * images.length;
  const dataOffset = headerSize + entriesSize;
  
  let totalDataSize = 0;
  for (const img of images) {
    totalDataSize += img.data.length;
  }

  const buffer = new ArrayBuffer(dataOffset + totalDataSize);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  // ICONDIR header
  view.setUint16(0, 0, true); // Reserved
  view.setUint16(2, 1, true); // Type: 1 = ICO
  view.setUint16(4, images.length, true); // Number of images

  // ICONDIRENTRY for each image + copy image data
  let currentOffset = dataOffset;
  for (let i = 0; i < images.length; i++) {
    const img = images[i];
    const entryOffset = headerSize + (i * entrySize);

    view.setUint8(entryOffset + 0, img.size === 256 ? 0 : img.size); // Width
    view.setUint8(entryOffset + 1, img.size === 256 ? 0 : img.size); // Height
    view.setUint8(entryOffset + 2, 0); // Color palette
    view.setUint8(entryOffset + 3, 0); // Reserved
    view.setUint16(entryOffset + 4, 1, true); // Color planes
    view.setUint16(entryOffset + 6, 32, true); // Bits per pixel
    view.setUint32(entryOffset + 8, img.data.length, true); // Size of image data
    view.setUint32(entryOffset + 12, currentOffset, true); // Offset to image data

    // Copy image data
    bytes.set(img.data, currentOffset);
    currentOffset += img.data.length;
  }

  return bytes;
}

/**
 * Generate site.webmanifest content
 */
export function generateWebManifest(appName: string = 'My App'): string {
  return JSON.stringify({
    name: appName,
    short_name: appName,
    icons: [
      {
        src: '/android-chrome-192x192.png',
        sizes: '192x192',
        type: 'image/png'
      },
      {
        src: '/android-chrome-512x512.png',
        sizes: '512x512',
        type: 'image/png'
      }
    ],
    theme_color: '#ffffff',
    background_color: '#ffffff',
    display: 'standalone'
  }, null, 2);
}

/**
 * Generate browserconfig.xml content
 */
export function generateBrowserConfig(): string {
  return `<?xml version="1.0" encoding="utf-8"?>
<browserconfig>
    <msapplication>
        <tile>
            <square150x150logo src="/mstile-150x150.png"/>
            <TileColor>#da532c</TileColor>
        </tile>
    </msapplication>
</browserconfig>`;
}

/**
 * Generate HTML snippet for including favicons
 */
export function generateHtmlSnippet(): string {
  return `<!-- Favicon -->
<link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png">
<link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png">
<link rel="shortcut icon" href="/favicon.ico">

<!-- Apple Touch Icon -->
<link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png">

<!-- Android Chrome -->
<link rel="manifest" href="/site.webmanifest">

<!-- Windows Tiles -->
<meta name="msapplication-TileColor" content="#da532c">
<meta name="msapplication-config" content="/browserconfig.xml">

<!-- Theme Color -->
<meta name="theme-color" content="#ffffff">`;
}

/**
 * Create a ZIP file with all favicon assets
 */
export async function createFaviconPackage(
  favicons: GeneratedFavicon[],
  appName: string = 'My App'
): Promise<Blob> {
  const zip = new JSZip();

  // Add PNG files
  for (const favicon of favicons) {
    zip.file(favicon.name, favicon.blob);
  }

  // Add ICO file
  const icoBlob = await generateIco(favicons);
  zip.file('favicon.ico', icoBlob);

  // Add manifest files
  zip.file('site.webmanifest', generateWebManifest(appName));
  zip.file('browserconfig.xml', generateBrowserConfig());

  // Add HTML snippet as readme
  zip.file('README.txt', `FAVICON PACKAGE
===============

Generated by ImageLab

FILES INCLUDED:
- favicon.ico (16x16, 32x32, 48x48)
- favicon-16x16.png
- favicon-32x32.png
- favicon-48x48.png
- apple-touch-icon.png (180x180)
- android-chrome-192x192.png
- android-chrome-512x512.png
- mstile-150x150.png
- site.webmanifest
- browserconfig.xml

INSTALLATION:
1. Copy all files to your website's root directory
2. Add the following HTML to your <head> section:

${generateHtmlSnippet()}
`);

  return zip.generateAsync({ type: 'blob' });
}
